import IndexNowSettings from "./IndexNowSettings";
import { cmsSettingsAccess } from "../../../helpers/cmsSettingsAccess";
import SiteStorage from "../../components/common/SiteStorage";
import { useCallback, useEffect, useRef, useState } from "react";
import CustomDomainSetup from "./CustomDomainSetup";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import Loading from "../../components/common/Loading";
import { Alert, Button, Col, Form, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { SiteService } from "../../../services/SiteService";
import { FormControlElement, ILanguage, IPage, ISiteDetails } from "../../../models/Interfaces";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import { faFileInvoice, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from '@form-validation/plugin-excluded';
import { ContentService } from "../../../services/ContentService";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { Link } from "react-router-dom";
import styles from "./siteDetails.module.scss";
import { centralEnabled } from "../../../services/centralAuth";

type initialDetails = {
  siteName: string,
  domain: string,
  subDomain: string
};

const SiteDetails = () => {
  const { canManageBilling } = cmsSettingsAccess();
  const [domainManaged, setDomainManaged] = useState(false);
  const domainManagedRef = useRef(false);
  const setDomainAvailability = useCallback((enabled: boolean) => {
    domainManagedRef.current = enabled;
    setDomainManaged(enabled);
  }, []);
  const updatePublishedDomain = useCallback((domain: string) => {
    setSiteDetails(previous => previous && previous.Domain !== domain ? { ...previous, Domain: domain } : previous);
  }, []);
  const [loading, setLoading] = useState<boolean>(true);
  const [siteDetails, setSiteDetails] = useState<ISiteDetails | null>(null);
  const [loadError, setLoadError] = useState("");
  const [customPages, setCustomPages] = useState<IPage[]>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);

  const [initialCheckDetails, setInitialCheckDetails] = useState<initialDetails | null>(null);

  const serviceRef = useRef<SiteService | null>(null);
  const contentSvcRef = useRef<ContentService | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const getSiteDetails = async (): Promise<void> => {
    if (serviceRef.current) {
      const res = await serviceRef.current.getSiteDetails();
      if (!res) throw new Error("Site details could not be loaded.");
      setSiteDetails(res);
      setInitialCheckDetails({
        siteName: res?.SiteName ?? "",
        domain: res?.Domain ?? "",
        subDomain: res?.SubDomain ?? ""
      });
    } else {
      setSiteDetails(null);
    }
  };

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getSiteLanguages();
      setLanguages(res);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>): void => {
    const { name, value } = event.target;
    if (name) {
      setSiteDetails(prev => ({
        ...prev as ISiteDetails,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;
    if (name) {
      setSiteDetails(prev => ({
        ...prev as ISiteDetails,
        [name]: checked
      }));
    }
  }

  const updatePropertyValue = (name: string, value: string): void => {
    if (name) {
      setSiteDetails(prev => ({
        ...prev as ISiteDetails,
        [name]: value
      }));
    }
  };

  const onFormSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (fvRef.current && serviceRef.current && siteDetails) {
      const valres = await fvRef.current.validate();

      if (valres === "Valid") {
        const res = await serviceRef.current.saveSiteDetails(siteDetails);
        if (res) {
          setSiteDetails(res);
        }
      }
    }
  };

  const createValidator = () : void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          SiteName: {
            validators: {
              notEmpty: { message: "Site name is required" },
            }
          },
          SubDomain: {
            validators: {
              notEmpty: { message: "A sub domain is required" },
              callback: {
                callback: async (field: any) => {
                  const validator = {
                    valid: field.value?.toLowerCase() === initialCheckDetails?.subDomain?.toLowerCase(),
                    message: ""
                  };

                  const checkres = await serviceRef.current?.checkSubDomain(field.value);

                  if (checkres !== null && checkres !== undefined) {
                    if (!checkres.Exists) {
                      validator.valid = true;
                      validator.message = "";
                    } else {
                      validator.message = checkres.Messages?.[0] ?? "Sub domain is already taken";
                    }
                  } else {
                    validator.message = "Failed to check sub domain"
                  }

                  return validator;
                }
              }
            }
          },
          Domain: {
            validators: {
              callback: {
                callback: async (field: any) => {
                  if (centralEnabled || domainManagedRef.current) return { valid: true, message: "" };
                  const validator = {
                    valid: (field.value?.toLowerCase() === initialCheckDetails?.domain?.toLowerCase()) || !field.value,
                    message: ""
                  };

                  const checkres = await serviceRef.current?.checkDomain(field.value);

                  if (checkres !== null && checkres !== undefined) {
                    if (!checkres.Exists) {
                      validator.valid = true;
                      validator.message = "";
                    } else {
                      validator.message = checkres.Messages?.[0] ?? "Domain is already taken";
                    }
                  } else {
                    validator.message = "Failed to check domain"
                  }

                  return validator;
                }
              }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          excluded: new Excluded(),
        }
      });
    }
  };

  useEffect(() => {
    if (!loading) {
      createValidator();
    }
  }, [loading]);

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new SiteService();
    }

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    const fetchData = async () => {
      try {
        await Promise.all([
          getSiteDetails(),
          getLanguages(),
          contentSvcRef.current!.getAllPages().then(pages => setCustomPages((pages ?? []).filter(page => !page.IsStaticPage)))
        ]);
      } catch (error) {
        setLoadError("Unable to load the site's settings. Please retry.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();

  }, []);

  return (
    <>
      {loading && (
        <Loading Message="loading site details..." SpinSize="5x" />
      )}

      {loadError && <Alert variant="danger">{loadError} <Button variant="link" onClick={() => window.location.reload()}>Retry</Button></Alert>}
      {!loading && !loadError && siteDetails && (
        <div className={styles.page}>
          <Form id="SiteDetailsForm" onSubmit={onFormSubmit} ref={formRef} className={styles.formStack}>
            <section className="app-list-section">
              <div className="app-list-section__header">
                <div>
                  <h2 className="app-list-section__title">Core Details</h2>
                  <p className="app-list-section__summary">
                    Manage the main site identity, default language, and domain settings used across the CMS and connected services.
                  </p>
                </div>
              </div>

              <div className={`app-list-section__body ${styles.sectionBody}`}>
                <SiteStorage settings />
                <Row className="g-3">
                  <Col sm={12} md={6}>
                    <FormLabel htmlFor="SiteName">Site name</FormLabel>
                    <FormControl
                      type="text"
                      name="SiteName"
                      id="SiteName"
                      value={siteDetails.SiteName}
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <FormLabel htmlFor="DefaultLanguage">Default page language</FormLabel>
                    <FormSelect
                      name="DefaultLanguage"
                      id="DefaultLanguage"
                      value={siteDetails.DefaultLanguage}
                      onChange={handleInputChange}
                      title="set default language for your site...">
                      <option value="">Select default site language...</option>
                      {
                        languages.map(lang => (
                          <option key={`LANG-${lang.ShortCode}`} value={lang.ShortCode}>{lang.Language}</option>
                        ))
                      }
                    </FormSelect>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col sm={12} md={6}>
                    <FormLabel htmlFor="SubDomain">Subdomain {"(<yoursite>.smoothbadgersites.com)"}</FormLabel>
                    <FormControl
                      type="text"
                      id="SubDomain"
                      name="SubDomain"
                      value={siteDetails.SubDomain}
                      placeholder="site sub domain (xxxx.smoothbadgersites.com)"
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <FormLabel htmlFor="Domain">Domain (e.g. mysite.com)</FormLabel>
                    <FormControl
                      type="text"
                      id="Domain"
                      name="Domain"
                      readOnly={centralEnabled || domainManaged}
                      title={centralEnabled || domainManaged ? "Manage this address in Own domain below." : undefined}
                      value={siteDetails.Domain ?? ""}
                      onChange={handleInputChange}
                      placeholder="site domain (e.g. mysite.com)"
                    />
                  </Col>
                </Row>
              </div>
            </section>

            <section className="app-list-section">
              <div className="app-list-section__header">
                <div>
                  <h2 className="app-list-section__title">Branding And Search</h2>
                  <p className="app-list-section__summary">
                    Set the core branding assets and search-facing text used when this site is presented across the web.
                  </p>
                </div>
              </div>

              <div className={`app-list-section__body ${styles.sectionBody}`}>
                <Row className="g-3">
                  <Col md={12}>
                    <FormLabel>Site Logo</FormLabel>
                    <div className={styles.mediaField}>
                      <SiteMediaPicker
                        onMediaClear={() => updatePropertyValue("Logo", "")}
                        onMediaSelect={(image) => updatePropertyValue("Logo", image)}
                        existingUrl={siteDetails.Logo}
                      />
                    </div>
                  </Col>
                  <Col md={12}>
                    <FormLabel>Site FavIcon</FormLabel>
                    <div className={styles.mediaField}>
                      <SiteMediaPicker
                        onMediaClear={() => updatePropertyValue("FavIcon", "")}
                        onMediaSelect={(image) => updatePropertyValue("FavIcon", image)}
                        existingUrl={siteDetails.FavIcon}
                      />
                    </div>
                  </Col>
                  <Col md={12}>
                    <FormLabel htmlFor="DomainVerification">Domain Verification Code (google/bing etc)</FormLabel>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="domain verification code/text"
                      id="DomainVerification"
                      name="DomainVerification"
                      value={siteDetails.DomainVerification ?? ""}
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12}>
                    <FormLabel htmlFor="Description">Site Description</FormLabel>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="site description for SEO"
                      id="Description"
                      name="Description"
                      value={siteDetails.Description ?? ""}
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12}>
                    <FormLabel htmlFor="About">Site About Text</FormLabel>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="site bio for search"
                      id="About"
                      name="About"
                      value={siteDetails.About ?? ""}
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col md={6}>
                    <FormLabel htmlFor="NotFoundPageId">Page Not Found</FormLabel>
                    <FormSelect id="NotFoundPageId" name="NotFoundPageId" value={siteDetails.NotFoundPageId ?? ""}
                      onChange={event => setSiteDetails(previous => previous && ({ ...previous,
                        NotFoundPageId: event.target.value ? Number(event.target.value) : null }))}>
                      <option value="">Use standard 404 page</option>
                      {siteDetails.NotFoundPageId && !customPages.some(page => page.Id === siteDetails.NotFoundPageId) &&
                        <option value={siteDetails.NotFoundPageId}>Unavailable page (standard fallback will be used)</option>}
                      {customPages.map(page => <option key={page.Id} value={page.Id}>{page.Title} ({page.Language})</option>)}
                    </FormSelect>
                    <Form.Text>Select a custom page and publish it to replace the standard page. Missing or unpublished pages use the fallback.</Form.Text>
                  </Col>
                  <Col md={6}>
                    <FormLabel htmlFor="ErrorPageId">Error Page</FormLabel>
                    <FormSelect id="ErrorPageId" name="ErrorPageId" value={siteDetails.ErrorPageId ?? ""}
                      onChange={event => setSiteDetails(previous => previous && ({ ...previous,
                        ErrorPageId: event.target.value ? Number(event.target.value) : null }))}>
                      <option value="">Use standard error page</option>
                      {siteDetails.ErrorPageId && !customPages.some(page => page.Id === siteDetails.ErrorPageId) &&
                        <option value={siteDetails.ErrorPageId}>Unavailable page (standard fallback will be used)</option>}
                      {customPages.map(page => <option key={page.Id} value={page.Id}>{page.Title} ({page.Language})</option>)}
                    </FormSelect>
                    <Form.Text>Select a custom page and publish it to replace the standard page. Missing or unpublished pages use the fallback.</Form.Text>
                  </Col>
                </Row>
              </div>
            </section>

            <section className="app-list-section">
              <div className="app-list-section__header">
                <div>
                  <h2 className="app-list-section__title">Sharing Preferences</h2>
                  <p className="app-list-section__summary">
                    Choose which channels should be available when content from this site is shared or promoted.
                  </p>
                </div>
              </div>

              <div className={`app-list-section__body ${styles.sectionBody}`}>
                <div className={styles.toggleGrid}>
                  <label className={styles.toggleCard} htmlFor="ShareFacebook">
                    <div>
                      <div className={styles.toggleTitle}>Facebook</div>
                      <div className={styles.toggleSummary}>Allow Facebook as a sharing destination.</div>
                    </div>
                    <FormCheckInput
                      name="ShareFacebook"
                      id="ShareFacebook"
                      checked={siteDetails.ShareFacebook}
                      onChange={handleCheckChange}
                      className={styles.toggleInput}
                    />
                  </label>
                  <label className={styles.toggleCard} htmlFor="ShareGoogle">
                    <div>
                      <div className={styles.toggleTitle}>Google</div>
                      <div className={styles.toggleSummary}>Allow Google-based sharing where supported.</div>
                    </div>
                    <FormCheckInput
                      name="ShareGoogle"
                      id="ShareGoogle"
                      checked={siteDetails.ShareGoogle}
                      onChange={handleCheckChange}
                      className={styles.toggleInput}
                    />
                  </label>
                  <label className={styles.toggleCard} htmlFor="ShareX">
                    <div>
                      <div className={styles.toggleTitle}>X (Twitter)</div>
                      <div className={styles.toggleSummary}>Allow X as a sharing destination for site content.</div>
                    </div>
                    <FormCheckInput
                      name="ShareX"
                      id="ShareX"
                      checked={siteDetails.ShareX}
                      onChange={handleCheckChange}
                      className={styles.toggleInput}
                    />
                  </label>
                  <label className={styles.toggleCard} htmlFor="ShareInstagram">
                    <div>
                      <div className={styles.toggleTitle}>Instagram</div>
                      <div className={styles.toggleSummary}>Allow Instagram sharing where the integration supports it.</div>
                    </div>
                    <FormCheckInput
                      name="ShareInstagram"
                      id="ShareInstagram"
                      checked={siteDetails.ShareInstagram}
                      onChange={handleCheckChange}
                      className={styles.toggleInput}
                    />
                  </label>
                </div>
              </div>
            </section>

            <section className="app-list-section">
              <div className="app-list-section__header">
                <div>
                  <h2 className="app-list-section__title">AI Settings</h2>
                  <p className="app-list-section__summary">
                    Configure the site-level AI connection so content users on this site can generate draft versions using the
                    site&apos;s own provider account and credits.
                  </p>
                </div>
              </div>

              <div className={`app-list-section__body ${styles.sectionBody}`}>
                <div className={styles.toggleGrid}>
                  <label className={styles.toggleCard} htmlFor="AIEnabled">
                    <div>
                      <div className={styles.toggleTitle}>Enable AI For This Site</div>
                      <div className={styles.toggleSummary}>
                        Allow users on this site to generate content with the configured provider credentials.
                      </div>
                    </div>
                    <FormCheckInput
                      name="AIEnabled"
                      id="AIEnabled"
                      checked={siteDetails.AIEnabled ?? false}
                      onChange={handleCheckChange}
                      className={styles.toggleInput}
                    />
                  </label>
                </div>

                {siteDetails.AIEnabled && (
                  <>
                    <div className={`${styles.toggleGrid} mt-3`}>
                      <label className={styles.toggleCard} htmlFor="AIAllowImageGeneration">
                        <div>
                          <div className={styles.toggleTitle}>Allow AI Image Generation</div>
                          <div className={styles.toggleSummary}>
                            Allow the connected AI provider to create images for page drafts before uploading them to site media.
                          </div>
                        </div>
                        <FormCheckInput
                          name="AIAllowImageGeneration"
                          id="AIAllowImageGeneration"
                          checked={siteDetails.AIAllowImageGeneration ?? false}
                          onChange={handleCheckChange}
                          className={styles.toggleInput}
                        />
                      </label>
                    </div>

                    <Row className="g-3 mt-1">
                      <Col sm={12} md={6}>
                        <FormLabel htmlFor="AIProvider">AI Provider</FormLabel>
                        <FormSelect
                          name="AIProvider"
                          id="AIProvider"
                          value={siteDetails.AIProvider ?? ""}
                          onChange={handleInputChange}
                          title="select ai provider">
                          <option value="">Select AI provider...</option>
                          <option value="openai">OpenAI</option>
                          <option value="azure-openai">Azure OpenAI</option>
                          <option value="xai">xAI</option>
                          <option value="anthropic">Anthropic</option>
                          <option value="google-gemini">Google Gemini</option>
                          <option value="openrouter">OpenRouter</option>
                          <option value="custom">Custom Compatible Endpoint</option>
                        </FormSelect>
                      </Col>
                      <Col sm={12} md={6}>
                        <FormLabel htmlFor="AIModel">Text Model / Deployment</FormLabel>
                        <FormControl
                          type="text"
                          id="AIModel"
                          name="AIModel"
                          value={siteDetails.AIModel ?? ""}
                          onChange={handleInputChange}
                          placeholder="e.g. gpt-4.1, gpt-4o, deployment-name"
                        />
                      </Col>
                    </Row>

                    <Row className="g-3">
                      <Col sm={12} md={6}>
                        <FormLabel htmlFor="AIEndpoint">AI Endpoint / Base Url</FormLabel>
                        <FormControl
                          type="text"
                          id="AIEndpoint"
                          name="AIEndpoint"
                          value={siteDetails.AIEndpoint ?? ""}
                          onChange={handleInputChange}
                          placeholder="Required for Azure OpenAI, xAI, or custom compatible providers"
                        />
                      </Col>
                      <Col sm={12} md={6}>
                        <FormLabel htmlFor="AIImageModel">Image Model / Deployment</FormLabel>
                        <FormControl
                          type="text"
                          id="AIImageModel"
                          name="AIImageModel"
                          value={siteDetails.AIImageModel ?? ""}
                          onChange={handleInputChange}
                          placeholder="Optional image generation model/deployment"
                        />
                      </Col>
                    </Row>

                    <Row className="g-3">
                      <Col sm={12}>
                        <FormLabel htmlFor="AIApiKey">Provider API Key</FormLabel>
                        <FormControl
                          type="password"
                          id="AIApiKey"
                          name="AIApiKey"
                          value={siteDetails.AIApiKey ?? ""}
                          onChange={handleInputChange}
                          placeholder="Store server-side encrypted when the API/database is updated"
                          autoComplete="new-password"
                        />
                        <div className="form-text">
                          This is intended to be a site-level credential so all permitted users on this site can use the same connected AI account.
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            </section>

            <section className="app-list-section">
              <div className={`app-list-section__body ${styles.footerActions}`}>
                <Button
                  type="submit"
                  variant="success"
                  className="app-table__add-button"
                  title="save details">
                  <FontAwesomeIcon icon={faSave} /> Save Details
                </Button>
                {canManageBilling && <Link
                  to="/account/details"
                  className={`btn btn-secondary ${styles.secondaryAction}`}
                  title="view in account">
                  <FontAwesomeIcon icon={faFileInvoice} /> View Account
                </Link>}
              </div>
            </section>
          </Form>
          <IndexNowSettings siteId={serviceRef.current?.siteKey ?? siteDetails.SiteId ?? siteDetails.Id} />
          <CustomDomainSetup publishedDomain={siteDetails.Domain} siteId={serviceRef.current?.siteKey ?? siteDetails.SiteId ?? siteDetails.Id}
            onAvailabilityChange={setDomainAvailability} onDomainChange={updatePublishedDomain} />
        </div>
      )}
    </>
  );
}

export default SiteDetails;
